import { useLocalSearchParams } from 'expo-router';
import { getToken } from 'features/v2a/receiver/helpers/Payments';
import {
  decodeJWT,
  ErrorMsgType,
  GetTokenResult,
} from 'features/v2a/receiver/helpers/V2A.helpers';
import { useTranslatedError } from 'features/v2a/receiver/hooks/useTranslatedError';
import { V2AReceiverContext } from 'features/v2a/receiver/providers/V2AReceiverContextProvider';
import { useCallback, useContext, useEffect, useState } from 'react';
import { updateStatsigCustomIDs } from 'services/statsig/statsigManager';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import {
  EnvironmentContext,
  EnvironmentContextProps,
} from 'shared/providers/environment';

interface PaymentLandingResponse {
  loading: boolean;
  tokenResponse?: GetTokenResult;
  tokenResponseError?: ErrorMsgType;
}

const usePaymentLanding = (): PaymentLandingResponse => {
  const { token } = useLocalSearchParams();

  const paymentToken = token as string;

  const { currentEnv } =
    useContext<EnvironmentContextProps>(EnvironmentContext);
  const { fetchIovationToken, getVaroSessionId } =
    useContext<AuthContextProps>(AuthContext);

  const { setPaymentDetails, paymentDetails } = useContext(V2AReceiverContext);

  const { getErrorMessage } = useTranslatedError();

  const [loading, setLoading] = useState<boolean>(false);
  const [tokenResponseError, setTokenResponseError] = useState<ErrorMsgType>();

  const loadToken = useCallback(async () => {
    const iovationToken = await fetchIovationToken();
    try {
      setLoading(true);
      const varoSessionId = await getVaroSessionId();
      const tokenData = await getToken(
        paymentToken,
        currentEnv,
        iovationToken,
        varoSessionId,
      );
      const decode = decodeJWT(tokenData as GetTokenResult);

      await updateStatsigCustomIDs({
        V2AReceiverId: decode.rid as string,
      });

      if (tokenData) {
        setPaymentDetails(tokenData);
      }
    } catch {
      setTokenResponseError(getErrorMessage('BAD_TOKEN'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentEnv,
    getErrorMessage,
    fetchIovationToken,
    paymentToken,
    setPaymentDetails,
  ]);

  useEffect(() => {
    if (!loading && !paymentDetails && !tokenResponseError) {
      void loadToken();
    }
  }, [loadToken, tokenResponseError, paymentDetails, loading]);

  return {
    loading,
    tokenResponse: paymentDetails,
    tokenResponseError,
  };
};

export default usePaymentLanding;
